import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

export type WidgetBadgeTone = 'active' | 'pending' | 'inactive' | 'info'

const toneStyles: Record<WidgetBadgeTone, { badge: string; dot: string }> = {
  active: {
    badge:
      'border-success-200 bg-success-50 text-success-700 dark:border-success-800 dark:bg-success-950 dark:text-success-300',
    dot: 'bg-success-500',
  },
  pending: {
    badge:
      'border-warning-200 bg-warning-50 text-warning-700 dark:border-warning-800 dark:bg-warning-950 dark:text-warning-300',
    dot: 'bg-warning-500',
  },
  inactive: {
    badge:
      'border-gray-300 bg-gray-100 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300',
    dot: 'bg-gray-500',
  },
  info: {
    badge:
      'border-[#b2ddff] bg-[#eff8ff] text-[#175cd3] dark:border-[#1849a9] dark:bg-[#102a56] dark:text-[#84caff]',
    dot: 'bg-[#175cd3]',
  },
}

interface WidgetDotBadgeProps {
  tone: WidgetBadgeTone
  children: React.ReactNode
  /** Omitted for badges that label a fact rather than a live state. */
  withDot?: boolean
  title?: string
}

export function WidgetDotBadge({ tone, children, withDot = true, title }: WidgetDotBadgeProps) {
  const styles = toneStyles[tone]

  return (
    <span
      title={title}
      className={cn(
        'inline-flex h-[22px] items-center gap-1 rounded-full border pr-2 text-xs font-medium',
        withDot ? 'pl-1.5' : 'px-2',
        styles.badge
      )}
    >
      {withDot && <span className={cn('size-1.5 shrink-0 rounded-full', styles.dot)} />}
      {children}
    </span>
  )
}

/** Pending means configured but not observed on a customer website yet. */
function deriveStatus(hasTemplate: boolean, websiteLive: boolean): Exclude<WidgetBadgeTone, 'info'> {
  if (!hasTemplate) return 'inactive'
  return websiteLive ? 'active' : 'pending'
}

interface WidgetStatusBadgeProps {
  hasTemplate: boolean
  websiteLive: boolean
}

export function WidgetStatusBadge({ hasTemplate, websiteLive }: WidgetStatusBadgeProps) {
  const { t } = useTranslation()

  const status = deriveStatus(hasTemplate, websiteLive)

  return (
    <WidgetDotBadge tone={status} title={t(`resources.widgets.status.${status}`)}>
      {t(`resources.widgets.status.${status}`)}
    </WidgetDotBadge>
  )
}
